export const NEXT_HIRE_ORIGIN = "https://nexthire.reyan.co.in";

const PRIVATE_PATH_PREFIXES = [
  "/applications",
  "/benefit-management",
  "/candidate",
  "/checkout",
  "/company/addCompany",
  "/company/listings",
  "/dashboard",
  "/invoice",
  "/jobs/apply",
  "/jobs/candidate",
  "/jobs/company",
  "/jobs/job-apply",
  "/jobs/listings",
  "/jobs/postjob",
  "/profile",
  "/report",
  "/session",
  "/skill-management",
  "/trash"
];

const LEGACY_JOB_PORTAL_HOST = "jobs.reyan.co.in";
const NON_PRODUCTION_HOSTS = new Set([
  "dev-nexthire.reyan.co.in",
  "test-jobs.reyan.co.in"
]);
const MALFORMED_LEGACY_JOB_PATH =
  /^\/jobs\/job-detail\/.*\/(?:session\/login\/company|index\.html)\/?$/i;
const RETIRED_LEGACY_JOB_IDS = new Set([
  "75c5af27-abd7-4acd-a97b-70447226913e"
]);
const ACTIVE_LEGACY_JOB_TITLES = {
  "0cb25a30-7006-46d1-9c61-ef361ce7a650": "Senior Manager",
  "cf9aaebf-8909-4275-a454-69d3eb193a58": "Senior Computer Vision Engineer",
  "e3a1e196-dfef-4391-9fad-cf35fc079d6b": "Tele caller",
  "e63e9d60-38d1-49a9-a61c-5f1341ef080b": "Motion Graphic Designer",
  "fe3e99c1-5be7-4d42-a290-a501e0a1dad5": "Digital Marketing Executive"
};

const RESERVED_JOB_ROUTES = new Set([
  "apply",
  "candidate",
  "company",
  "job-apply",
  "job-detail",
  "job-detail-qa",
  "listings",
  "postjob"
]);

const CATEGORY_TERM_BY_SLUG = {
  "UI-UX-Designer": "UI/UX Designer"
};

const CATEGORY_SLUG_BY_TERM = Object.fromEntries(
  Object.entries(CATEGORY_TERM_BY_SLUG).map(([slug, term]) => [term, slug])
);

const CITY_NAME_BY_ALIAS = {
  bangalore: "Bengaluru"
};

const TEMPLATE_VALUE_PATTERN = /\{[^}]+\}/;

function safeDecode(value = "") {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function cleanValue(value = "") {
  const cleaned = String(value).replace(/\+/g, " ").trim();
  return TEMPLATE_VALUE_PATTERN.test(cleaned) ? "" : cleaned;
}

function normalizeCity(value = "") {
  const cleaned = cleanValue(value);
  return CITY_NAME_BY_ALIAS[cleaned.toLowerCase()] || cleaned;
}

function getFirstValue(primaryParams, legacyParams, keys) {
  for (const key of keys) {
    const value = primaryParams.get(key) ?? legacyParams.get(key);
    if (value) return cleanValue(value);
  }

  return "";
}

function getCategoryPath(jobTitle) {
  if (!jobTitle) return "/jobs";

  const knownSlug = CATEGORY_SLUG_BY_TERM[jobTitle];
  if (knownSlug) return `/jobs/${knownSlug}`;

  // A slash cannot safely be URL-encoded inside a path segment on every host.
  if (jobTitle.includes("/")) return "/jobs";

  return `/jobs/${encodeURIComponent(jobTitle)}`;
}

export function isPrivateIndexingRoute(pathname = "") {
  return PRIVATE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isNonProductionIndexingHost(hostname = "") {
  return NON_PRODUCTION_HOSTS.has(hostname.toLowerCase());
}

export function getLegacyUrlRedirect({
  hostname = "",
  pathname = "/",
  search = "",
  hash = ""
} = {}) {
  const isLegacyHost = hostname.toLowerCase() === LEGACY_JOB_PORTAL_HOST;
  const isMalformedLegacyJobPath = MALFORMED_LEGACY_JOB_PATH.test(pathname);
  const lastPathSegment = pathname.split("/").filter(Boolean).pop()?.toLowerCase() || "";
  const isRetiredLegacyJob = isLegacyHost && RETIRED_LEGACY_JOB_IDS.has(lastPathSegment);
  const activeLegacyJobTitle = isLegacyHost && ACTIVE_LEGACY_JOB_TITLES[lastPathSegment];

  if (!isLegacyHost && !isMalformedLegacyJobPath) return "";

  // These historic URLs lost the job ID when a relative login link was
  // appended to the job-detail URL, so there is no valid detail page to map.
  if (isMalformedLegacyJobPath || isRetiredLegacyJob) {
    return `${NEXT_HIRE_ORIGIN}/jobs`;
  }

  if (activeLegacyJobTitle) {
    return `${NEXT_HIRE_ORIGIN}${buildJobDetailPath(activeLegacyJobTitle, lastPathSegment)}`;
  }

  return `${NEXT_HIRE_ORIGIN}${pathname || "/"}${search}${hash}`;
}

export function buildJobSearchUrl({
  jobTitle = "",
  city = "",
  type = "",
  jobId = "",
  locationType = "",
  company = "",
  exp = "",
  salary = "",
  page = ""
} = {}) {
  const cleanedJobTitle = cleanValue(jobTitle);
  const cleanedCity = normalizeCity(city);
  const path = getCategoryPath(cleanedJobTitle);
  const params = new URLSearchParams();

  if (cleanedJobTitle && path === "/jobs") params.set("job_title", cleanedJobTitle);
  if (cleanedCity) params.set("city", cleanedCity);
  if (cleanValue(type)) params.set("type", cleanValue(type));
  if (cleanValue(jobId)) params.set("job_id", cleanValue(jobId));
  if (cleanValue(locationType)) params.set("location_type", cleanValue(locationType));
  if (cleanValue(company)) params.set("company", cleanValue(company));
  if (cleanValue(exp)) params.set("exp", cleanValue(exp));
  if (cleanValue(salary)) params.set("salary", cleanValue(salary));
  if (cleanValue(page)) params.set("page", cleanValue(page));

  const query = params.toString();
  return `${path}${query ? `?${query}` : ""}`;
}

export function parseJobSearchLocation(pathname = "", search = "") {
  const isJobsRoot = pathname === "/jobs" || pathname === "/jobs/";
  const isSingleJobSearchSegment = pathname.startsWith("/jobs/") &&
    !pathname.slice("/jobs/".length).includes("/");

  if (!isJobsRoot && !isSingleJobSearchSegment) {
    return { isPublicJobsPage: false };
  }

  const encodedSegment = isSingleJobSearchSegment ? pathname.slice("/jobs/".length) : "";
  const decodedSegment = safeDecode(encodedSegment);
  const [rawPathTerm = "", ...legacyParts] = decodedSegment.split("&");
  const mappedPathTerm = CATEGORY_TERM_BY_SLUG[rawPathTerm] || rawPathTerm;

  if (RESERVED_JOB_ROUTES.has(mappedPathTerm.toLowerCase())) {
    return { isPublicJobsPage: false };
  }

  const params = new URLSearchParams(search);
  const legacyParams = new URLSearchParams(legacyParts.join("&"));
  const rawQueryJobTitle = params.get("job_title") || legacyParams.get("job_title") || "";
  const hasTemplateValue = [rawQueryJobTitle, ...params.values(), ...legacyParams.values()].some(
    (value) => TEMPLATE_VALUE_PATTERN.test(value)
  );

  const jobTitle = cleanValue(rawQueryJobTitle || mappedPathTerm);
  const rawCity = getFirstValue(params, legacyParams, ["city", "location"]);
  const city = normalizeCity(rawCity);
  const type = getFirstValue(params, legacyParams, ["type"]);
  const jobId = getFirstValue(params, legacyParams, ["job_id"]);
  const locationType = getFirstValue(params, legacyParams, ["location_type"]);
  const company = getFirstValue(params, legacyParams, ["company"]);
  const exp = getFirstValue(params, legacyParams, ["exp"]);
  const salary = getFirstValue(params, legacyParams, ["salary"]);
  const page = getFirstValue(params, legacyParams, ["page"]);

  const normalizedUrl = hasTemplateValue
    ? "/jobs"
    : buildJobSearchUrl({
        jobTitle,
        city,
        type,
        jobId,
        locationType,
        company,
        exp,
        salary,
        page
      });

  const primaryFilters = [jobTitle, city, type].filter(Boolean);
  const hasSecondaryFilters = Boolean(jobId || locationType || company || exp || salary || page);
  const isIndexable = !hasTemplateValue && primaryFilters.length <= 1 && !hasSecondaryFilters;

  let canonicalPath = "/jobs";
  if (jobTitle) canonicalPath = buildJobSearchUrl({ jobTitle });
  else if (city) canonicalPath = buildJobSearchUrl({ city });
  else if (type) canonicalPath = buildJobSearchUrl({ type });

  const hasLegacyPathParams = legacyParts.length > 0;
  const hasLegacyLocationParam = params.has("location");
  const hasCityAlias = Boolean(rawCity && rawCity !== city);
  const queryTitleCanMoveToPath = Boolean(
    rawQueryJobTitle && (CATEGORY_SLUG_BY_TERM[jobTitle] || !jobTitle.includes("/"))
  );

  return {
    isPublicJobsPage: true,
    jobTitle,
    city,
    type,
    jobId,
    locationType,
    company,
    exp,
    salary,
    page,
    isIndexable,
    hasTemplateValue,
    canonicalPath,
    normalizedUrl,
    shouldRedirect:
      hasTemplateValue ||
      hasLegacyPathParams ||
      hasLegacyLocationParam ||
      hasCityAlias ||
      queryTitleCanMoveToPath
  };
}

export function getJobSearchSeo(pathname = "", search = "") {
  const state = parseJobSearchLocation(pathname, search);
  if (!state.isPublicJobsPage) return null;

  const { jobTitle, city, type } = state;
  const titleParts = [type, jobTitle].filter(Boolean).join(" ");
  const subject = titleParts ? `${titleParts} Jobs` : "Jobs";
  const locationSuffix = city ? ` in ${city}` : " in India";
  const heading = `${subject}${locationSuffix}`;
  const title = `${heading} | NextHire`;
  const description = `Find current ${subject.toLowerCase()}${locationSuffix} on NextHire. Explore job descriptions, required skills, companies, locations, and apply online.`;
  const keywords = [
    jobTitle && `${jobTitle} jobs`,
    city && `jobs in ${city}`,
    type && `${type} jobs`,
    "jobs in India",
    "NextHire jobs"
  ]
    .filter(Boolean)
    .join(", ");

  return {
    ...state,
    title,
    heading,
    description,
    keywords,
    canonicalUrl: `${NEXT_HIRE_ORIGIN}${state.canonicalPath}`,
    robots: state.isIndexable ? "index, follow" : "noindex, follow"
  };
}

export function buildJobDetailPath(jobTitle = "job", jobId = "") {
  const slug = String(jobTitle || "job")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "job";

  return `/jobs/job-detail/${slug}/${encodeURIComponent(jobId)}`;
}

export function isJobPostingIndexable(job, now = new Date()) {
  if (!job || job.jp_is_deleted === 1 || job.jp_is_enabled === false || job.jp_closed_at) {
    return false;
  }

  if (job.jp_end_date) {
    const endDate = new Date(job.jp_end_date);
    if (!Number.isNaN(endDate.getTime()) && endDate < now) return false;
  }

  return true;
}
